import React, { useState } from 'react';
import { supabase } from '../../../shared/lib/supabase';
import DatePicker from '../../../shared/components/ui/DatePicker';
import SearchableDropdown from '../../../shared/components/ui/SearchableDropdown';
import { useTournaments } from '../../../shared/hooks/useTournaments';
import { useTeams } from '../../../shared/hooks/useTeams';
import { useSeasonsByTournament } from '../../../shared/hooks/useSeasons';
import { accessControlService } from '../../../shared/services/accessControl';

interface CreateMatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  mode?: 'create' | 'edit';
  matchData?: any; // For editing existing match
}

interface CreateMatchForm {
  name: string;
  tournament_id: string;
  season_id: string;
  home_team_id: string;
  away_team_id: string;
  date: string;
  time: string;
  venue: string;
}

const CreateMatchModal: React.FC<CreateMatchModalProps> = ({ isOpen, onClose, onSuccess, mode = 'create', matchData }) => {
  const [formData, setFormData] = useState<CreateMatchForm>({
    name: '',
    tournament_id: '',
    season_id: '',
    home_team_id: '',
    away_team_id: '',
    date: '',
    time: '12:00', // Default time since time picker is hidden
    venue: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { tournaments, loading: tournamentsLoading } = useTournaments();
  const { teams, loading: teamsLoading } = useTeams();
  const { seasons, loading: seasonsLoading } = useSeasonsByTournament(formData.tournament_id);

  // Load match data for editing
  React.useEffect(() => {
    if (mode === 'edit' && matchData) {
      let startDate = new Date(); // Default to current date
      
      if (matchData.start_date) {
        const parsedDate = new Date(matchData.start_date);
        if (!isNaN(parsedDate.getTime())) { // Check if date is valid
          startDate = parsedDate;
        } else {
          console.warn("Invalid start_date received for match:", matchData.start_date);
        }
      } else {
        console.warn("No start_date received for match:", matchData);
      }

      setFormData({
        name: matchData.name || '',
        tournament_id: matchData.tournament_id || '',
        season_id: matchData.season_id || '',
        home_team_id: matchData.home_team_id || '',
        away_team_id: matchData.away_team_id || '',
        date: startDate.toISOString().split('T')[0] || '',
        time: startDate.toTimeString().slice(0, 5) || '',
        venue: matchData.venue || ''
      });
    } else {
      // Reset form for create mode
      setFormData({
        name: '',
        tournament_id: '',
        season_id: '',
        home_team_id: '',
        away_team_id: '',
        date: '',
        time: '12:00', // Default time since time picker is hidden
        venue: ''
      });
    }
  }, [mode, matchData]);

  // Track if form has been modified for edit mode
  const [hasChanges, setHasChanges] = React.useState(false);
  const [originalData, setOriginalData] = React.useState<CreateMatchForm | null>(null);

  React.useEffect(() => {
    if (mode === 'edit' && matchData) {
      setOriginalData(formData);
      setHasChanges(false);
    }
  }, [mode, matchData]);

  React.useEffect(() => {
    if (mode === 'edit' && originalData) {
      const hasFormChanged = JSON.stringify(formData) !== JSON.stringify(originalData);
      setHasChanges(hasFormChanged);
    }
  }, [formData, originalData, mode]);

  const handleInputChange = (field: keyof CreateMatchForm, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      // Clear season when tournament changes
      ...(field === 'tournament_id' ? { season_id: '' } : {})
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.name || !formData.tournament_id || !formData.season_id || !formData.home_team_id || 
          !formData.away_team_id || !formData.date || !formData.time) {
        throw new Error('Please fill in all required fields');
      }

      // Validate that home and away teams are different
      if (formData.home_team_id === formData.away_team_id) {
        throw new Error('Home and away teams must be different');
      }

      // Combine date and time into start_date (convert local time to UTC)
      const localDateTime = new Date(`${formData.date}T${formData.time}:00`);
      
      // Create match data
      const matchDataToSave = {
        ...(mode === 'edit' && matchData ? { id: matchData.id } : {}),
        name: formData.name,
        tournament_id: formData.tournament_id,
        season_id: formData.season_id,
        home_team_id: formData.home_team_id,
        away_team_id: formData.away_team_id,
        start_date: localDateTime.toISOString(), // This converts local time to UTC
        venue: formData.venue || null,
        status: 'upcoming',
        home_score: 0,
        away_score: 0,
        active: true
      };

      if (mode === 'edit' && matchData) {
        // Update existing match
        const { data, error: updateError } = await supabase
          .from('matches')
          .update(matchDataToSave)
          .eq('id', matchData.id)
          .select();

        if (updateError) {
          throw new Error(updateError.message);
        }

        if (!data || data.length === 0) {
          throw new Error('No match found with the given ID');
        }
      } else {
        // Create new match
        const { data, error: insertError } = await supabase
          .from('matches')
          .insert([matchDataToSave])
          .select();

        if (insertError) {
          throw new Error(insertError.message);
        }

        if (!data || data.length === 0) {
          throw new Error('Failed to create match');
        }
      }

      // Clear cache to ensure fresh data is loaded
      accessControlService.clearCache();
      
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create match');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto relative">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {mode === 'edit' ? 'Edit Match' : 'Create New Match'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Match Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Match Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="w-full text-sm text-gray-900 bg-white border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter match name"
              required
            />
          </div>

          {/* Tournament */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tournament *
            </label>
            <SearchableDropdown
              value={formData.tournament_id}
              onChange={(value) => handleInputChange('tournament_id', value)}
              options={tournaments}
              placeholder="Select tournament"
              className="w-full"
              loading={tournamentsLoading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Season *
            </label>
            <SearchableDropdown
              value={formData.season_id}
              onChange={(value) => handleInputChange('season_id', value)}
              options={seasons.map(season => ({
                id: season.id,
                name: season.name,
                value: season.id,
                label: season.name
              }))}
              placeholder="Select season"
              className="w-full"
              loading={seasonsLoading}
            />
          </div>

          {/* Teams */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Home Team *
              </label>
              <SearchableDropdown
                value={formData.home_team_id}
                onChange={(value) => handleInputChange('home_team_id', value)}
                options={teams}
                placeholder="Select home team"
                className="w-full"
                loading={teamsLoading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Away Team *
              </label>
              <SearchableDropdown
                value={formData.away_team_id}
                onChange={(value) => handleInputChange('away_team_id', value)}
                options={teams}
                placeholder="Select away team"
                className="w-full"
                loading={teamsLoading}
              />
            </div>
          </div>

          {/* Date Only - Time picker hidden due to timer bug */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date *
            </label>
            <DatePicker
              value={formData.date}
              onChange={(value) => handleInputChange('date', value)}
              placeholder="Select date"
              className="w-full"
            />
          </div>

          {/* Venue */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Venue
            </label>
            <input
              type="text"
              value={formData.venue}
              onChange={(e) => handleInputChange('venue', e.target.value)}
              className="w-full text-sm text-gray-900 bg-white border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter venue (optional)"
            />
          </div>


          {/* Error Message */}
          {error && (
            <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || (mode === 'edit' && !hasChanges)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (mode === 'edit' ? 'Updating...' : 'Creating...') : (mode === 'edit' ? 'Update Match' : 'Create Match')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateMatchModal;
